// THE adapter between the source-candidates wire filters and the filter shape the
// reused UI speaks.
//
// The search modal does not invent a filter model. It borrows the CRM matches tab's
// `MatchFilters` — because that is what `MatchFilterChips` edits and what
// `ListingsFiltersDrawer` (through `matchFiltersToSearchState`) edits — and translates
// it here, in one file, in both directions. Nothing else under seller/ may convert
// between the two shapes; if a conversion is needed elsewhere, export it from here.
//
// ── the one rule worth stating twice ─────────────────────────────────────────────────
// The forward direction goes through `buildMatchSearchRequest`, the SAME pure function
// the listings board and the matches tab use to turn those controls into a query. That
// is deliberate rather than convenient: it means "max rooms at the top notch means no
// cap", the typeof guards that keep string-typed contact values out of the request, and
// every future refinement of what a control MEANS, are shared instead of reimplemented.
// A second copy of that logic would drift silently — the symptom being a filter that
// narrows differently here than on the board it looks identical to.
//
// Its `origin` argument and its `maxResults`/`page` are discarded: this endpoint pins
// the origins server-side (see CANDIDATE_ORIGINS — admitting `webot` would make the ads
// the picker exists to find silently vanish) and owns its own paging.
import { buildMatchSearchRequest } from "components/_dashboard/contacts/detail/tabs/matches/matchesSearch";
import type { MatchFilters } from "components/_dashboard/contacts/detail/tabs/matches/contactMatchFilters";
import type { FrozenFilters } from "components/_dashboard/listingsMatch/ListingsFiltersDrawer";
import type {
  Room,
  Floor,
  ParkingSpot,
  GenericPropertyDetails,
  ProcessedListingAddress,
  REST_schema
} from "../../../../@types/sharedSchema";

/**
 * Controls the full filters drawer must NOT render here.
 *
 * `listingOrigins` is the only one, and it is a correctness constraint rather than a
 * layout choice: the server pins the search to yad2+madlan because
 * `resolveClaimedSourceIds` injects the caller's claimed-ad exclusion list whenever
 * `webot` is among the requested origins. An origin control would therefore be a
 * control that either does nothing (we ignore it) or, if ever honoured, deletes the
 * search's own results.
 *
 * Everything else the drawer offers is genuinely applied by the endpoint, so it stays
 * visible and working.
 */
export const CANDIDATE_FROZEN_FILTERS: FrozenFilters = { listingOrigins: true };

type WireFilters = REST_schema.ListingSourceCandidateFilters;

/**
 * `MatchFilters` + the free-text box → the wire filters.
 *
 * EVERY key is emitted, using `null` for "not filtering on this". That is the contract
 * the endpoint documents: a key the client omits falls back to the SEEDED value, so an
 * omitted `minPrice` would silently restore the ±35% window the realtor just cleared.
 * The client holds the whole filter set — it is what the chips render — so it sends the
 * whole filter set.
 */
export function wireFiltersFromMatchFilters(filters: MatchFilters, queryText: string): WireFilters {
  // The origin argument is discarded; only the FILTER half of the request is read.
  const request = buildMatchSearchRequest(filters, "yad2", { queryText });

  return {
    listingListedFor: request.listingListedFor,
    searchTerm: request.searchTerm ?? null,
    // An empty array is NOT null: it means "she removed the area filter", which the
    // endpoint answers with `needsNarrowing` rather than by re-seeding the city.
    locations: request.locations ?? [],
    minRooms: request.minRooms ?? null,
    maxRooms: request.maxRooms ?? null,
    minPrice: request.minPrice ?? null,
    maxPrice: request.maxPrice ?? null,
    minFloor: request.minFloor ?? null,
    maxFloor: request.maxFloor ?? null,
    minParkingSpots: request.minParkingSpots ?? null,
    maxParkingSpots: request.maxParkingSpots ?? null,
    minSquareMeter: request.minSquareMeter ?? null,
    maxSquareMeter: request.maxSquareMeter ?? null,
    propertyTypes: request.propertyTypes ?? [],
    adTypes: request.adTypes ?? [],
    agencyNames: request.agencyNames ?? [],
    sellerNames: request.sellerNames ?? [],
    booleanFilters: request.booleanFilters ?? {},
    isAssetExclusive: request.isAssetExclusive ?? false,
    onlyPriceChanged: request.onlyPriceChanged ?? false,
    hidePotentiallyRemovedListings: request.hidePotentiallyRemovedListings ?? false,
    publishedFromMs: request.publishedFromMs ?? null,
    publishedToMs: request.publishedToMs ?? null
  };
}

/**
 * The wire filters the server actually applied → `MatchFilters`.
 *
 * Used exactly once per opening, on the SEED: the first request carries no `filters` at
 * all, the server answers with the seed it chose, and that answer becomes the chip row's
 * initial state. Seeding client-side instead would mean keeping a second copy of the
 * price-window policy in the browser, and the picker does not even hold the listing's
 * city — it is handed a listing id.
 *
 * ⚠ `hidePotentiallyRemovedListings` is mapped EXPLICITLY to a boolean and never left
 * undefined. In `MatchFilters`' own vocabulary `undefined` means ON — the discovery
 * surface has always age-hidden stale market ads — and that default is exactly wrong
 * here: an ad for a property she is claiming may well not have been re-seen for a week,
 * and hiding it removes the only row she came for.
 *
 * `publishedDateRange` has no inverse and does not need one: the seed never carries a
 * published window, so there is never a from/to here to turn back into a preset.
 */
export function matchFiltersFromWire(applied: WireFilters): MatchFilters {
  const locations = (applied.locations ?? []).map(
    (location) =>
      ({
        ...(location.city ? { city: location.city } : {}),
        ...(location.neighborhood ? { neighborhood: location.neighborhood } : {}),
        ...(location.street ? { street: location.street } : {}),
        ...(location.canonicalLocationId
          ? { canonicalLocationId: location.canonicalLocationId }
          : {}),
        ...(location.canonicalLevel ? { canonicalLevel: location.canonicalLevel } : {})
      } as ProcessedListingAddress)
  );

  return {
    listedFor: applied.listingListedFor === "rent" ? "rent" : "sale",
    locations,
    minPrice: applied.minPrice ?? undefined,
    maxPrice: applied.maxPrice ?? undefined,
    minRooms: (applied.minRooms ?? undefined) as Room | undefined,
    maxRooms: (applied.maxRooms ?? undefined) as Room | undefined,
    minFloors: (applied.minFloor ?? undefined) as Floor | undefined,
    maxFloors: (applied.maxFloor ?? undefined) as Floor | undefined,
    minSquareMeter: applied.minSquareMeter ?? undefined,
    maxSquareMeter: applied.maxSquareMeter ?? undefined,
    propertyTypes: applied.propertyTypes ?? [],
    booleanProps: (applied.booleanFilters ?? {}) as Partial<GenericPropertyDetails>,

    minParkingSpots: (applied.minParkingSpots ?? undefined) as ParkingSpot | undefined,
    maxParkingSpots: (applied.maxParkingSpots ?? undefined) as ParkingSpot | undefined,
    adTypes: applied.adTypes ?? [],
    agencyNames: applied.agencyNames ?? [],
    sellerNames: applied.sellerNames ?? [],
    isAssetExclusive: applied.isAssetExclusive ?? false,
    onlyPriceChanged: applied.onlyPriceChanged ?? false,
    hidePotentiallyRemovedListings: applied.hidePotentiallyRemovedListings === true
  };
}

/**
 * Which chips are showing a value the SERVER seeded from the listing rather than one the
 * realtor chose.
 *
 * The chip row is keyed by control (`price` / `rooms` / `locations` / `types`), while the
 * wire reports individual filter keys — this collapses the second onto the first so the
 * modal can say "seeded from this listing" next to the controls it is actually true of.
 * A seeded filter that is not visibly labelled as such is the failure mode that made
 * composite keys unusable in production: an invisible narrowing that silently excludes
 * the true match.
 */
export type SeededChipId = "price" | "rooms" | "locations" | "listedFor";

const SEEDED_KEY_TO_CHIP: Record<string, SeededChipId> = {
  minPrice: "price",
  maxPrice: "price",
  minRooms: "rooms",
  maxRooms: "rooms",
  locations: "locations",
  listingListedFor: "listedFor"
};

export function seededChipIds(seededFilterKeys: readonly string[]): SeededChipId[] {
  const chips = new Set<SeededChipId>();
  seededFilterKeys.forEach((key) => {
    const chip = SEEDED_KEY_TO_CHIP[key];
    if (chip) chips.add(chip);
  });
  return [...chips];
}
